import React from 'react';
import cx from 'classnames';
import useImageDimensions from 'paper/hooks/imageDimensions';
import { getImageUrl, ptToPx } from 'paper/utils/helpers';
import type { WCardTemplateElementView } from '@zola/svc-web-api-ts-client';
import type { ImageCrop, DefaultCropTypes } from '../../util/types';
import styles from './ImageElement.module.less';

export interface ImageElementProps {
  element?: WCardTemplateElementView;
  crop?: ImageCrop;
  defaultCropType?: DefaultCropTypes;
  photoURL?: string;
  uploadcareFile?: { cdnUrl: string };
  isRounded?: boolean;
  isFolded?: boolean;
}

const ImageElement: React.FC<ImageElementProps> = ({
  element,
  crop,
  defaultCropType,
  photoURL,
  uploadcareFile,
  isRounded = false,
  isFolded = false,
}) => {
  const photoSize = useImageDimensions(photoURL);

  if (!element) {
    return null;
  }
  const { customizable, overlay, layer = 0 } = element;
  let { height: elHeight = 0 } = element;
  const isFoldedCardElement = isFolded && !customizable && !overlay;

  if (isFoldedCardElement) elHeight /= 2;

  const divStyle = {
    zIndex: layer + 1,
    left: `${element.x}pt`,
    top: isFoldedCardElement ? `${elHeight}pt` : `${element.y}pt`,
    width: `${element.width}pt`,
    height: `${elHeight}pt`,
  };

  let imgURL;

  const croppedImageStyle: {
    backgroundPosition?: string;
    backgroundSize?: string;
    backgroundImage?: string;
  } = {};

  if (uploadcareFile) {
    // Uploadcare image
    imgURL = uploadcareFile.cdnUrl;
  } else if (photoURL) {
    let photoCrop: ImageCrop;

    const { height: imageHeight = 0, width: imageWidth = 0 } = photoSize;
    const containerHeight = ptToPx(elHeight);
    const containerWidth = ptToPx(element.width);

    const ratioWidth = containerWidth / imageWidth;
    const ratioHeight = containerHeight / imageHeight;

    if (defaultCropType === 'cover') {
      // calculations to mimic background-size: cover
      const coverRatio = Math.max(ratioWidth, ratioHeight);
      const finalHeight = imageHeight * coverRatio;
      const finalWidth = imageWidth * coverRatio;
      photoCrop = {
        height: finalHeight,
        width: finalWidth,
        x: (containerWidth - finalWidth) / 2,
        y: (containerHeight - finalHeight) / 2,
      };
    } else {
      // fit according to crop prop passed; else, just fits to width of template
      const defaultCrop = {
        x: 0,
        y: 0,
        width: 1,
        height: 1,
      };

      photoCrop = crop
        ? {
            width: imageWidth / crop.width,
            height: imageHeight / crop.height,
            x: (crop.x * containerWidth) / crop.width,
            y: (crop.y * containerHeight) / crop.height,
          }
        : defaultCrop;
    }

    croppedImageStyle.backgroundPosition = `${photoCrop.x}px ${photoCrop.y}px`;
    croppedImageStyle.backgroundSize =
      defaultCropType === 'cover'
        ? `${photoCrop.width}px`
        : `${Math.round(100 * photoCrop.width)}%`;

    imgURL = photoURL;
  } else if (element.image_id) {
    // Default element image
    imgURL = getImageUrl(element.image_id);
  }
  croppedImageStyle.backgroundImage = `url(${imgURL}?w=200)`;
  croppedImageStyle.backgroundPosition = isFoldedCardElement ? 'bottom' : '';

  return (
    <div
      className={cx(styles.imageLayer, {
        [styles.rounded]: isRounded,
        [styles.shadow]: isFoldedCardElement,
      })}
      style={{ ...divStyle, ...croppedImageStyle }}
    />
  );
};

export default ImageElement;
